import api from "../../../services/api";
import uploadService from "@/services/upload-service";


export default {
  namespaced: true,
  state: {
    isLoading: false,
    bookList: {
      data: [],
      links: {
        first: null,
        last: null,
        prev: null,
        next: null,
      },
      meta: {
        current_page: 1,
        from: 1,
        last_page: 1,
        path: null,
        per_page: 10,
        to: 0,
        total: 0,
      },
    },
    bookInfo: null,
    bookDeliveryList: [],
    parcelPreview: [],
    purchaseDeliveryReportByBook: [],
    purchaseDeliveryReportByCourse: [],
  },
  mutations: {
    INIT() { },
    SET_LOADING_STATUS(state, status) {
      state.isLoading = status === null ? !state.isLoading : status;
    },
    SET_BOOK_LIST(state, data) {
      state.bookList = data;
    },
    SET_BOOK_INFO(state, data) {
      state.bookInfo = data;
    },
    SET_BOOK_DELIVERY_LIST(state, data) {
      state.bookDeliveryList = data;
    },
    SET_PARCEL_PREVIEW(state, data) {
      state.parcelPreview = data;
    },
    SET_PURCHASE_DELIVERY_REPORT_BY_BOOK(state, data) {
      state.purchaseDeliveryReportByBook = data;
    },
    SET_PURCHASE_DELIVERY_REPORT_BY_COURSE(state, data) {
      state.purchaseDeliveryReportByCourse = data.data;
    }
  },
  getters: {
    isLoading(state) {
      return state.isLoading;
    },
    bookList(state) {
      return state.bookList;
    },
    bookInfo(state) {
      return state.bookInfo;
    },
    bookDeliveryList(state) {
      return state.bookDeliveryList;
    },
    parcelPreview(state) {
      return state.parcelPreview;
    },
    purchaseDeliveryReportByBook(state) {
      return state.purchaseDeliveryReportByBook;
    },
    purchaseDeliveryReportByCourse(state) {
      return state.purchaseDeliveryReportByCourse;
    }
  },
  actions: {
    init(context) {
      context.commit("INIT");
    },

    async fetchBookList(context, params = {}) {
      context.commit("SET_LOADING_STATUS", true);

      return await api
        .get(`/v1/books`, { params })
        .then(({ data }) => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("SET_BOOK_LIST", data);
        })
        .catch((error) => {
          context.commit("SET_LOADING_STATUS", false);
          console.error("fetchBookList: ", error);
          throw error;
        });
    },

    async fetchBookInfo(context, bookId) {
      context.commit("SET_LOADING_STATUS", true);

      return await api
        .get(`/v1/books/${bookId}`)
        .then(({ data }) => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("SET_BOOK_INFO", data.data);
        })
        .catch((error) => {
          context.commit("SET_LOADING_STATUS", false);
          console.error("fetchBookInfo: ", error);
          throw error;
        });
    },

    async createBook(context, payload) {
      context.commit("SET_LOADING_STATUS", true);

      let bookCoverPath = null;
      if (payload.coverImage && payload.coverImage instanceof File) {
        const bookPayload = {
          companyId: payload.companyId,
          imageFile: payload.coverImage,
        };

        try {
          const response = await uploadService.uploadBookCover(bookPayload);
          bookCoverPath = response.data.path || response.data.image_url;
        } catch (error) {
          console.error("uploadBookCover error: ", error);
          console.error("uploadBookCover response: ", error.response);
          context.commit("SET_LOADING_STATUS", false);
          throw new Error("Failed to upload book cover image");
        }
      }

      const bookData = {
        book_code: payload.book_code,
        book_name: payload.book_name,
        book_remark: payload.book_remark || "",
        book_coverImagePath: bookCoverPath,
      };

      return await api
        .post(`/v1/books`, bookData)
        .then(({ data }) => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("SET_BOOK_INFO", data.data);
          return data.data;
        })
        .catch((error) => {
          context.commit("SET_LOADING_STATUS", false);
          console.error("createBook: ", error);
          throw error;
        });
    },

    async updateBook(context, payload) {
      context.commit("SET_LOADING_STATUS", true);

      let bookCoverPath = null;
      if (payload.coverImage && payload.coverImage instanceof File) {
        const bookPayload = {
          companyId: payload.companyId,
          imageFile: payload.coverImage,
        };

        try {
          const response = await uploadService.uploadBookCover(bookPayload);
          bookCoverPath = response.data.path || response.data.image_url;
        } catch (error) {
          console.error("uploadBookCover error: ", error);
          console.error("uploadBookCover response: ", error.response);
          context.commit("SET_LOADING_STATUS", false);
          throw new Error("Failed to upload book cover image");
        }
      }

      const bookData = {
        book_code: payload.book_code,
        book_name: payload.book_name,
        book_remark: payload.book_remark || "",
        book_coverImagePath: bookCoverPath,
      };

      return await api
        .put(`/v1/books/${payload.id}`, bookData)
        .then(({ data }) => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("SET_BOOK_INFO", data.data);
        })
        .catch((error) => {
          context.commit("SET_LOADING_STATUS", false);
          console.error("updateBook: ", error);
          throw error;
        });
    },

    async deleteBook(context, bookId) {
      context.commit("SET_LOADING_STATUS", true);

      return await api
        .delete(`/v1/books/${bookId}`)
        .then(() => {
          context.commit("SET_LOADING_STATUS", false);
        })
        .catch((error) => {
          context.commit("SET_LOADING_STATUS", false);
          console.error("deleteBook: ", error);
          throw error;
        });
    },

    async validateBookCode(context, { bookCode, bookId = null }) {
      const payload = {
        book_code: bookCode
      };

      // Include book_id if provided (for edit mode)
      if (bookId) {
        payload.book_id = bookId;
      }

      return await api
        .post('/v1/books/validate-code', payload)
        .then(({ data }) => {
          return {
            valid: data.valid,
            message: data.message
          };
        })
        .catch((error) => {
          console.error("validateBookCode: ", error);
          // Return error response if available, otherwise return null
          if (error.response?.data) {
            return {
              valid: error.response.data.valid,
              message: error.response.data.message
            };
          }
          throw error;
        });
    },

    async fetchBookDeliveryList(context, params = {}) {
      context.commit("SET_LOADING_STATUS", true);

      return await api
        .get(`/v1/book-delivery/details`, { params })
        .then(({ data }) => {
          context.commit("SET_LOADING_STATUS", false);
          context.commit("SET_BOOK_DELIVERY_LIST", data.data.map(item => ({
            ...item,
            isEditing: false
          }))
          );
        })
        .catch((error) => {
          context.commit("SET_LOADING_STATUS", false);
          console.error("fetchBookDeliveryList: ", error);
          throw error;
        });
    },
    async updateBookDelivery(context, payload) {
      await api.put(`/v1/book-delivery/${payload.bdId}`, payload.attribute);
    },
    async importPacelBookDelivery(context, formData) {
      try {
        const response = await api.post(
          `/v1/book-delivery/flash-express-import-preview`,
          formData,
        );
        context.commit("SET_PARCEL_PREVIEW", response.data.matched);
        return response.data;
      } catch (error) {
        console.error("importPacelBookDelivery: ", error);
        throw error;
      }
    },
    async updatePacelData(context, params) {
      await api.put(`/v1/book-delivery/flash-express-bulk-update`, { items: params });
    },
    async fetchPurchaseDeliveryReportByBook(context, params) {
      return await api
        .get(`/v1/course-order-report/sum-book-delivery`, { params })
        .then(({ data }) => {
          context.commit("SET_PURCHASE_DELIVERY_REPORT_BY_BOOK", data);
        })
        .catch((error) => {
          console.error("fetchPurchaseDeliveryReportByBook: ", error);
          throw error;
        });
    },
    async fetchPurchaseDeliveryReportByCourse(context, params) {
      return await api
        .get(`/v1/books/sum-course-order`, { params })
        .then(({ data }) => {
          context.commit("SET_PURCHASE_DELIVERY_REPORT_BY_COURSE", data);
        })
        .catch((error) => {
          console.error("fetchPurchaseDeliveryReportByCourse: ", error);
          throw error;
        });
    }
  },
};
